/**
 * WORK&Hub 에 직접 묻는 것들
 * ---------------------------------------------------------------------------
 * ## 왜 제품 API 를 거치지 않나
 * 내 계정(이름·이메일·연락처·비밀번호·접속 기기)의 원본은 Hub 다. 제품
 * API 를 한 번 거치면 같은 값을 두 번 옮겨 적게 되고, 그 사이에 한쪽이
 * 낡는다. 토큰은 이미 메모리에 있으니 바로 묻는다.
 *
 * ## 왜 제품에 이 화면이 필요한가
 * WORK&Hub 콘솔은 **기업 관리자만** 들어간다(2026-10-27). 일지만 쓰는
 * 사람은 Hub 에 못 들어가므로, 비밀번호를 바꿀 자리가 제품 안에 없으면
 * 바꿀 방법 자체가 사라진다.
 *
 * ## 이름과 이메일은 여기서 못 바꾼다
 * 둘 다 Hub 가 원본이고 이메일은 로그인 아이디이기도 하다. 제품에서
 * 고쳐 봐야 다음 동기화 때 되돌아간다 — 바뀌지도 않을 값을 바꿀 수
 * 있는 것처럼 보여 주지 않는다. 보여 주되 잠근다.
 */

import { ensureAccessToken, forceRefresh } from './auth';

const PLATFORM_API =
  import.meta.env.VITE_PLATFORM_API_BASE_URL ?? 'http://localhost:3000/api';

export interface PlatformProfile {
  userId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  empNo: string | null;
  departmentName: string | null;
  companyName: string | null;
  mfaEnabled?: boolean;
  lastLoginAt?: string | null;
}

export interface PlatformSession {
  sessionId: string;
  deviceLabel: string | null;
  clientIp: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

/**
 * Hub 를 부른다.
 *
 * 401 이면 한 번만 토큰을 다시 받아 재시도한다. 제품 토큰은 짧아서,
 * 화면을 띄워 두고 커피를 마시고 온 사람이 저장을 누르면 거의 늘 만료다.
 * 거기서 "로그인이 필요합니다" 를 띄우면 쓰던 입력이 날아간다.
 */
async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const token = retried ? await forceRefresh() : await ensureAccessToken();
  if (!token) throw new Error('로그인이 필요합니다.');

  const res = await fetch(`${PLATFORM_API}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && !retried) return call<T>(method, path, body, true);

  if (!res.ok) {
    /*
     * 서버가 준 말을 그대로 올린다. 「저장에 실패했습니다」로 뭉개면
     * "비밀번호 규칙이 안 맞는다" 같은 고칠 수 있는 이유가 사라진다.
     */
    const text = await res.text();
    let message = `요청이 실패했습니다. (HTTP ${res.status})`;
    try {
      const parsed = JSON.parse(text) as { message?: string };
      if (parsed.message) message = parsed.message;
    } catch {
      /* 본문이 JSON 이 아니면 기본 문구를 쓴다 */
    }
    throw new Error(message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const platform = {
  me: () => call<PlatformProfile>('GET', '/auth/me'),

  /** 바꿀 수 있는 것은 연락처뿐이다. 빈 값은 「지우라」는 뜻이라 null 로 보낸다. */
  updatePhone: (phone: string | null) =>
    call<void>('PATCH', '/auth/me', { phone: phone?.trim() ? phone.trim() : null }),

  /** confirmPassword 도 함께 보낸다 — Hub 가 두 값을 맞춰 본다. */
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) =>
    call<void>('POST', '/auth/password/change', {
      currentPassword,
      newPassword,
      confirmPassword,
    }),

  sessions: () => call<PlatformSession[]>('GET', '/auth/sessions'),

  revokeSession: (sessionId: string) =>
    call<void>('DELETE', `/auth/sessions/${sessionId}`),
};
